/**
 * Envio de um lote do pool para `POST /api/v2/activity` (docs/telemetria.md, seção 4).
 *
 * A resposta vira uma decisão para o pool: apagar o lote, dividir, segurar até a mesma pessoa
 * logar de novo, ou tentar mais tarde. Nunca lança.
 */
import { apiRequest } from '../apiClient';
import type { ActivityEnvelope } from './outbox';

export const ACTIVITY_PATH = 'api/v2/activity';

export interface ActivityBatchMeta {
  id: string;
  activeMs?: Record<string, number>;
  pool?: { pending: number; oldestAgeMs: number; retries: number; dropped: number };
}

export interface ActivityBatchBody {
  batch: ActivityBatchMeta;
  events: ActivityEnvelope[];
}

export type SendOutcome =
  | { kind: 'sent'; accepted: number; duplicates: number; rejected: number }
  /** 413: o painel divide o lote. */
  | { kind: 'split' }
  /** 401: o lote fica preso a quem o gerou até essa pessoa estar logada de novo. */
  | { kind: 'held' }
  /** Rede, 429, 5xx ou qualquer outra recusa: tenta de novo mais tarde, sem perder nada. */
  | { kind: 'retry'; retryAfterMs?: number };

/** gzip pelo próprio navegador; sem `CompressionStream`, vai sem compressão. */
async function gzip(text: string): Promise<ArrayBuffer | null> {
  if (typeof CompressionStream === 'undefined' || typeof Blob === 'undefined') return null;
  try {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
    return await new Response(stream).arrayBuffer();
  } catch {
    return null;
  }
}

function retryAfterOf(details: unknown): number | undefined {
  if (!details || typeof details !== 'object') return undefined;
  const seconds = (details as Record<string, unknown>)['retryAfterSeconds'];
  return typeof seconds === 'number' && seconds > 0 ? seconds * 1000 : undefined;
}

/**
 * `keepalive`: envio de saída da página, sem compressão (a compressão é assíncrona e a página
 * pode acabar antes) e dentro do limite de 64 KB do navegador.
 */
export async function sendActivityBatch(
  body: ActivityBatchBody,
  options: { keepalive?: boolean } = {},
): Promise<SendOutcome> {
  try {
    const text = JSON.stringify(body);
    const compressed = options.keepalive ? null : await gzip(text);
    const result = await apiRequest<{ accepted?: number; duplicates?: number; rejected?: number }>(
      ACTIVITY_PATH,
      {
        method: 'POST',
        rawBody: compressed ?? text,
        headers: {
          'Content-Type': 'application/json',
          ...(compressed ? { 'Content-Encoding': 'gzip' } : {}),
        },
        ...(options.keepalive ? { keepalive: true } : {}),
      },
    );
    if (result.ok) {
      return {
        kind: 'sent',
        accepted: Number(result.data.accepted ?? 0),
        duplicates: Number(result.data.duplicates ?? 0),
        rejected: Number(result.data.rejected ?? 0),
      };
    }
    if (result.status === 401) return { kind: 'held' };
    if (result.status === 413) return { kind: 'split' };
    const retryAfterMs = retryAfterOf(result.details);
    return retryAfterMs ? { kind: 'retry', retryAfterMs } : { kind: 'retry' };
  } catch {
    return { kind: 'retry' };
  }
}
